import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { fadeAnimation } from 'src/animations';
import { LocationService } from '../../../services/location.service';
import { Address } from 'src/app/lib/location/classes';

@Component({
  selector: 'app-select-address',
  templateUrl: './select-address.component.html',
  styleUrls: ['./select-address.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectAddressComponent),
      multi: true
    }
  ],
  animations: [fadeAnimation]
})
export class SelectAddressComponent implements ControlValueAccessor, OnDestroy, OnInit {

  @Input() label: string = "Adresse wählen";
  @Input() appearance: 'standard' | 'fill' | 'outline' = 'fill';

  initialValue = undefined;

  formGroup: FormGroup;

  keyupDebounceSub: Subscription = null;
  currentSearchSub: Subscription = null;

  currentAddress: Address = null;
  addresses: Address[] = [];

  @ViewChild('input', { static: true }) public input: ElementRef<HTMLInputElement>;
  inputFocussed: BehaviorSubject<boolean> = new BehaviorSubject(false);

  _subscriptions: Subscription[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _locationService: LocationService,
    private _snackBar: MatSnackBar
  ) {
    this.formGroup = _formBuilder.group({
      'filter': null,
      'value': null
    });
    this._subscriptions.push(...[
      this
        .filterControl
        .valueChanges
        .subscribe(
          () => {
            if (this.currentSearchSub) this.currentSearchSub.unsubscribe();
            this.currentSearchSub = null;
            this.keyupDebounceSub = timer(2000).subscribe(() => this.searchAddress());
          }
        ),
      this
        .valueControl
        .valueChanges
        .subscribe(
          () => this.setCurrentAddress()
        ),
      this
        .inputFocussed
        .subscribe((value: boolean) => {
          this.filterControl.setValue(!value && this.currentAddress ? this.displayAddress(this.currentAddress) : null, {
            'emitEvent': false
          });
        })
    ]);
  }

  clearValue(event?) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.setAddress(null);
  }

  displayAddress(address: Address): string {
    if (!address) return '';
    let pre = `${address.addressstreet ? address.addressstreet : ''} ${address.addresshousenumber ? address.addresshousenumber : ''}`;
    let suf = `${address.addresszipcode ? address.addresszipcode : ''} ${address.addresscity ? address.addresscity : ''}`;
    if (pre.trim().length > 0 && suf.trim().length > 0) return `${pre}, ${suf}`;
    else if (pre.trim().length > 0) return `${pre}`;
    else if (suf.trim().length > 0) return `${suf}`;
    return '';
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
    if (this.keyupDebounceSub) this.keyupDebounceSub.unsubscribe();
    this.keyupDebounceSub = null;
    if (this.currentSearchSub) this.currentSearchSub.unsubscribe();
    this.currentSearchSub = null;
  }

  ngOnInit(): void {

  }

  onBlur(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    this.inputFocussed.next(false);
  }

  onFocus(event) {
    if (event) event.preventDefault();
    this.inputFocussed.next(true);
  }

  onSearchClicked(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.currentSearchSub) this.currentSearchSub.unsubscribe();
    this.currentSearchSub = null;
    this.searchAddress();
  }

  public onTouched: () => void = () => { };

  registerOnChange(fn: any): void {
    this
      ._subscriptions
      .push(
        this
          .valueControl
          .valueChanges
          .subscribe(fn)
      );
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  searchAddress() {
    if (this.keyupDebounceSub) this.keyupDebounceSub.unsubscribe();
    this.keyupDebounceSub = null;
  }

  setAddress(address?: Address) {
    address ? this.valueControl.setValue(address.addressid) : this.valueControl.setValue(null, { 'emitValue': false });
    this.setCurrentAddress();
    this.input.nativeElement.blur();
    this.inputFocussed.next(false);
  }

  setCurrentAddress() {
    /*
    if (this.valueControl.value) {
      this
        ._locationService
        .resolveAddress(this.valueControl.value)
        .subscribe(
          (result: Address) => {
            this.currentAddress = result;
            if (!this.inputFocussed.value) this.filterControl.setValue(this.displayAddress(result));
          },
          (error: HttpErrorResponse) => this._snackBar.open(error.message, 'Adresse laden')
        );
    } else {
      this.currentAddress = null;
    }
    */
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  writeValue(val: any): void {
    this
      .valueControl
      .setValue(val);

    if (typeof this.initialValue === 'undefined') this.initialValue = val;
  }

  get filterControl(): AbstractControl {
    return this.formGroup.controls['filter'];
  }
  get valueControl(): AbstractControl {
    return this.formGroup.controls['value'];
  }

}
